/**
 * REST-клиент `/api/v1` (Bearer JWT). Access-токен живёт ТОЛЬКО в памяти;
 * refresh — HttpOnly cookie (сервер ставит сам). 401 → один refresh-повтор.
 */

import { API_BASE } from "./config";

export interface HelpTopic { key: string; label: string }

export interface ChatMessage {
  id: number;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
  origin_channel: "telegram" | "pwa";
  client_msg_id?: string | null;
  help_topics?: HelpTopic[];
}

export interface AuthUser {
  tg_id: number;
  first_name: string;
  username: string;
  role: string;
}

export interface SendResult {
  ok: boolean;
  deduped: boolean;
  reply: string | null;
  mirrored: boolean;
  user_msg_id: number | null;
  reply_msg_id: number | null;
  operation?: OperationCard | null;
  help_topics?: HelpTopic[];
}

// ── §6.3 операции (Confirm-Gate, D2) ─────────────────────────────────────────

export interface OperationCard {
  operation_id: string;
  tool: string;
  kind: string;
  payload_hash: string;
  status: string;
  channel: string;
  draft: Record<string, unknown>;
  created_at: string;
  expires_at: string;
}

// ── §6.4–6.6 read-модели (PWA-04) ────────────────────────────────────────────

export interface UserContext {
  found: boolean;
  tg_id: number;
  name?: string | null;
  age?: number | null;
  height_cm?: number | null;
  start_weight_kg?: number | null;
  goal_weight_kg?: number | null;
  intent?: string | null;
  goal_text?: string | null;
  consent_at?: string | null;
  coach_memory?: Record<string, unknown>;
  memory?: { semantic?: unknown[]; episodic?: unknown[]; structured?: unknown };
  ui_lang?: string | null;
  role?: string;
  is_admin?: boolean;
  is_coach?: boolean;
  is_partner?: boolean;
}

export interface Checkin {
  at: string | null;
  weight_kg: number | null;
  mood: string | null;
  adherence_note: string | null;
  raw_text: string | null;
  meta: Record<string, unknown>;
}

export interface TodayAggregate {
  date: string;
  tz: string;
  program: {
    found: boolean;
    program_title?: string;
    phase?: string;
    phase_state?: Record<string, unknown>;
    current_rules?: unknown[];
    phases?: Array<Record<string, unknown>>;
  };
  upcoming: Array<{
    id: number;
    fire_at: string | null;
    kind: string;
    purpose: string | null;
    status: string;
  }>;
  last_checkin: Checkin | null;
  recovery_recent?: boolean;
}

export interface MonthAggregate {
  ok: boolean;
  days: number;
  weight_delta_kg: number | null;
  checkins: number;
  active_days: number;
  hard_days: number;
  returns: number;
  workouts: { completed: number; missed: number; upcoming: number };
}

export interface CoachClient {
  client_tg_id: number;
  name?: string | null;
  status?: string;
  started_at?: string | null;
}

export interface PdConsentState {
  ok: boolean;
  state: "hard_first" | "hard_repeat" | "soft" | "pass" | "age_gate";
  version: string;
  texts: { message: string; button?: string; decline_button?: string };
  // Возрастной вход (W3): серверные действия и контекст экрана.
  allowed?: boolean;
  status?: string | null;
  reason?: string | null;
  jurisdiction?: string | null;
  nonce?: string | null;
  text_hash?: string | null;
  exact_age?: number | null;
  age_conflict?: boolean;
  actions?: AgeGateAction[];
}

export interface AgeGateAction {
  action_id: string;
  kind: "select" | "accept" | "decline" | "correct";
  label: string;
  group_id?: string | null;
}

export interface AgeActionResponse {
  ok: boolean;
  already?: boolean;
  allowed: boolean;
  accepted?: boolean;
  status?: string | null;
  reason?: string | null;
  error?: string | null;
  state?: string;
  nonce?: string | null;
  text_hash?: string | null;
  texts?: { message: string };
  actions?: AgeGateAction[];
}

let accessToken: string | null = null;
let sessionGeneration = 0;
const SIGNED_OUT_KEY = "nika.signed_out";
let refreshFlight: Promise<boolean> | null = null;
let logoutFlight: Promise<void> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasSession(): boolean {
  return accessToken !== null;
}

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown) {
    super(`API ${status}`);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const resp = await fetch(API_BASE + path, {
    ...init,
    headers,
    credentials: "include", // refresh-cookie
  });
  if (resp.status === 401 && retry) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(path, init, false);
    throw new ApiError(401, "unauthorized");
  }
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new ApiError(resp.status, body);
  return body as T;
}

function tryRefresh(): Promise<boolean> {
  if (localStorage.getItem(SIGNED_OUT_KEY) === "1") return Promise.resolve(false);
  if (!refreshFlight) refreshFlight = performRefresh().finally(() => { refreshFlight = null; });
  return refreshFlight;
}

async function performRefresh(): Promise<boolean> {
  const generation = sessionGeneration;
  try {
    const resp = await fetch(API_BASE + "/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
      signal: AbortSignal.timeout(15000),
    });
    if (!resp.ok) return false;
    const body = await resp.json();
    if (generation !== sessionGeneration) return false;
    accessToken = body.access_token;
    return Boolean(accessToken);
  } catch {
    return false;
  }
}

// ── auth ─────────────────────────────────────────────────────────────────────

export async function loginWithTelegram(
  fields: Record<string, unknown>,
): Promise<AuthUser> {
  const body = await request<{
    access_token: string;
    user: AuthUser;
  }>("/auth/telegram-login", { method: "POST", body: JSON.stringify(fields) });
  accessToken = body.access_token;
  localStorage.removeItem(SIGNED_OUT_KEY);
  return body.user;
}

/** Пытается восстановить сессию по refresh-cookie (вход после перезагрузки). */
export async function restoreSession(): Promise<boolean> {
  return tryRefresh();
}

export function logout(): Promise<void> {
  if (logoutFlight) return logoutFlight;
  sessionGeneration++;
  accessToken = null;
  // Локальная отметка не даёт восстановить сессию даже при сбое сети на выходе.
  localStorage.setItem(SIGNED_OUT_KEY, "1");
  logoutFlight = (async () => {
    // Браузер применяет Set-Cookie до чтения JSON: завершаем старый refresh
    // перед удалением cookie и открытием нового входа.
    await refreshFlight;
    await fetch(API_BASE + "/auth/logout", {
      method: "POST", credentials: "include", signal: AbortSignal.timeout(15000),
    }).catch(() => undefined);
  })().finally(() => { logoutFlight = null; });
  return logoutFlight;
}

// ── chat ─────────────────────────────────────────────────────────────────────

export function getMessages(after = 0, limit = 50): Promise<{ messages: ChatMessage[]; welcome?: { coach_name: string | null; trial_days: number | null } | null }> {
  return request(`/chat/messages?after=${after}&limit=${limit}`);
}

export function sendMessage(
  content: string,
  clientMsgId: string,
  mediaIds: string[] = [],
): Promise<SendResult> {
  return request<SendResult>("/chat/send", {
    method: "POST",
    body: JSON.stringify({
      content,
      client_msg_id: clientMsgId,
      ...(mediaIds.length ? { media_ids: mediaIds } : {}),
    }),
  });
}

export function accessTokenForStream(): string | null {
  return accessToken;
}

// ── §6.3 операции (Confirm-Gate поверх D2/K1) ────────────────────────────────

export function getActiveOperation(): Promise<{
  ok: boolean;
  operation: OperationCard | null;
}> {
  return request("/operations/active");
}

/** Подтверждение: сервер сверяет payload_hash (I-5) и исполняет тул. */
export function confirmOperation(
  opId: string,
  payloadHash: string,
): Promise<{ ok: boolean; receipt?: Record<string, unknown>; error?: string }> {
  return request(`/operations/${opId}/confirm`, {
    method: "POST",
    body: JSON.stringify({ payload_hash: payloadHash }),
  });
}

/** Отказ: черновик → 'rejected' (D2). reason — только в аудит-лог. */
export function rejectOperation(
  opId: string,
  reason = "user_rejected",
): Promise<{ ok: boolean; error?: string }> {
  return request(`/operations/${opId}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// ── §6.4 участник (чтение Server-Side Truth) ─────────────────────────────────

export function getMeContext(): Promise<UserContext> {
  return request("/me/context");
}

export function getMeCheckins(limit = 30): Promise<{ checkins: Checkin[] }> {
  return request(`/me/checkins?limit=${limit}`);
}

export interface CoachTask {
  task_id: number;
  kind: "task" | "instruction";
  title: string;
  description: string | null;
  payload: Record<string, unknown>;
  due_kind: "next_training" | "date" | null;
  due_at: string | null;
  status: "active" | "done" | "cancelled";
}

export function getMyTasks(): Promise<{
  ok?: boolean;
  tasks?: CoachTask[];
  active_count?: number;
}> {
  return request("/me/tasks");
}

export function getToday(): Promise<TodayAggregate> {
  return request("/today");
}

// Кэш «сегодня» (30с): экран Сегодня + чипсы чата читают один и тот же
// агрегат, чтобы не дёргать /today дважды за переключение вкладки.
let todayCache: { at: number; data: TodayAggregate } | null = null;

export function fetchTodayCached(maxAgeMs = 30_000): Promise<TodayAggregate> {
  if (todayCache && Date.now() - todayCache.at < maxAgeMs) {
    return Promise.resolve(todayCache.data);
  }
  return getToday().then((data) => {
    todayCache = { at: Date.now(), data };
    return data;
  });
}

export function getMeProgressMonth(days = 30): Promise<MonthAggregate> {
  return request(`/me/progress/month?days=${days}`);
}

export function getMeProgress(metric = "weight"): Promise<{
  ok: boolean;
  summary?: string;
  metric?: string;
  weight_points?: number;
  activity_points?: number;
  reason?: string;
  message?: string;
}> {
  return request(`/me/progress?metric=${encodeURIComponent(metric)}`);
}

/** URL PNG-графика (Bearer не живёт в URL — fetch blob с токеном). */
export async function getProgressChartPng(metric = "weight"): Promise<Blob | null> {
  const headers = new Headers();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const resp = await fetch(
    API_BASE + `/me/progress/chart.png?metric=${encodeURIComponent(metric)}`,
    { headers, credentials: "include" },
  );
  if (!resp.ok) return null;
  return resp.blob();
}

// ── §6.5 программы (чтение) ──────────────────────────────────────────────────

export function getProgramsActive(): Promise<Record<string, unknown>> {
  return request("/programs/active");
}

export function getProgramsVersions(): Promise<{
  found: boolean;
  slug?: string;
  active_version?: string;
  versions: Array<{ id: number; version: string; title: string; created_at: string | null }>;
}> {
  return request("/programs/versions");
}

// ── §6.6 контур тренера (чтение) ─────────────────────────────────────────────

export function getCoachClients(): Promise<{ ok?: boolean; clients?: CoachClient[]; error?: string }> {
  return request("/coach/clients");
}

export function getClientBrief(clientTg: number): Promise<Record<string, unknown>> {
  return request(`/coach/clients/${clientTg}/brief`);
}

// ── служебные write-пути (§7.1 язык; PD-гейт auth-контура) ───────────────────

export function setPreferences(lang: string): Promise<{ ok: boolean; lang: string }> {
  return request("/me/preferences", {
    method: "POST",
    body: JSON.stringify({ lang }),
  });
}

export function getPdConsent(lang?: string): Promise<PdConsentState> {
  const q = lang ? `?lang=${encodeURIComponent(lang)}` : "";
  return request(`/me/pd-consent${q}`);
}

export function decidePdConsent(
  decision: "accept" | "decline",
  lang?: string,
): Promise<{ ok: boolean; accepted: boolean; declined_text?: string; state?: string }> {
  return request("/me/pd-consent", {
    method: "POST",
    body: JSON.stringify({ decision, lang }),
  });
}

/** Действие возрастного экрана (W3): серверный action_id (ag:*). */
export function decideAgeAction(
  actionId: string,
  lang?: string,
): Promise<AgeActionResponse> {
  return request("/me/pd-consent", {
    method: "POST",
    body: JSON.stringify({ action_id: actionId, lang }),
  });
}

// ── §6.8 приватность (PD + visual-consent) ───────────────────────────────────

export interface PrivacyConsents {
  pd: {
    found: boolean;
    consent_at: string | null;
    consent_version: string | null;
  };
  visual: {
    granted: boolean;
    granted_at: string | null;
    body_upload_visible: boolean;
  };
  privacy: Record<string, unknown>;
}

export function getPrivacyConsents(): Promise<PrivacyConsents> {
  return request("/privacy/consents");
}

/** grant — после PD; revoke не блокируется и purg'ит фото тела из MinIO (§9.3). */
export function setVisualConsent(action: "grant" | "revoke"): Promise<{
  ok: boolean;
  consent: boolean;
  consent_at?: string;
  body_photos_purged?: number;
  note?: string;
}> {
  return request("/privacy/consents/visual", {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

// ── §6.11 Integration Hub + privacy-категории (Phase 15 W6, GAD-08) ─────────

export interface IntegrationConnection {
  id: number;
  provider: string;
  channel: string;
  status: string;
  scopes: Record<string, unknown>;
  external_account_id: string;
  last_sync_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export function getIntegrations(): Promise<{
  ok: boolean;
  connections: IntegrationConnection[];
}> {
  return request("/integrations/connections");
}

/** Старт OAuth: сервер отдаёт URL Strava authorize (state CSRF — серверный). */
export function stravaOauthStart(): Promise<{
  ok: boolean;
  url?: string;
  state?: string;
  error?: string;
}> {
  return request("/integrations/strava/oauth/start");
}

export function stravaSync(): Promise<{
  ok: boolean;
  inserted?: number;
  duplicates_exact?: number;
  skipped_no_consent?: number;
  pages?: number;
  rate_limited?: boolean;
  error?: string;
}> {
  return request("/integrations/strava/sync", { method: "POST", body: "{}" });
}

/** Отзыв доступа: стоп синхронизации немедленно + удаление данных источника. */
export function revokeConnection(id: number): Promise<{
  ok: boolean;
  status?: string;
  events_purged?: number;
  error?: string;
}> {
  return request(`/integrations/connections/${id}/revoke`, {
    method: "POST",
    body: "{}",
  });
}

export function getMeActivities(limit = 200): Promise<{
  ok: boolean;
  events: Array<Record<string, unknown>>;
}> {
  return request(`/me/activities?limit=${limit}`);
}

/** GAD-08: категории гаджетов + «передавать тренеру», default OFF;
 *  неизвестные ключи сервер отбрасывает. */
export function setPrivacyFlags(patch: Record<string, boolean>): Promise<{
  ok: boolean;
  privacy: Record<string, boolean>;
  error?: string;
}> {
  return request("/privacy/consents/privacy", {
    method: "POST",
    body: JSON.stringify(patch),
  });
}

// ── §6.8 hard-delete + экспорт (E2, DoD-8) ───────────────────────────────────

export interface DataExport {
  found: boolean;
  tg_id: number;
  profile?: Record<string, unknown>;
  checkins?: Array<Record<string, unknown>>;
  messages?: Array<Record<string, unknown>>;
  exported_at?: string;
}

export function exportMyData(): Promise<DataExport> {
  return request("/privacy/export");
}

/** Шаг 1 (без фразы) → confirmation_required + confirm_phrase + window_s;
 *  шаг 2 (с фразой) → deleted:true либо ok:false + error. */
export interface DeleteConfirmStep {
  ok: boolean;
  error?: string;
  confirmation_required?: boolean;
  confirm_phrase?: string;
  window_s?: number;
  deleted?: boolean;
  coach_deleted?: boolean;
  media_purged?: number;
}

export function startDeleteMyAccount(): Promise<DeleteConfirmStep> {
  return request("/privacy/delete-my-account", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** Шаг 2: фраза сверяется server-side (echo-free — подтверждение содержимым). */
export function confirmDeleteMyAccount(
  phrase: string,
): Promise<DeleteConfirmStep> {
  return request("/privacy/delete-my-account", {
    method: "POST",
    body: JSON.stringify({ confirm_phrase: phrase }),
  });
}

// ── §6.9 медиа (D3: presign → PUT → complete) ────────────────────────────────

export type MediaCategory =
  | "meal"
  | "body_progress"
  | "program_document"
  | "voice_note";

export interface MediaInfo {
  media_id: string;
  category: MediaCategory;
  mime_type: string;
  size_bytes: number;
  sha256: string;
  created_at: string;
}

export interface PresignResult {
  media_id: string;
  upload_url: string;
  method: string;
  headers: Record<string, string>;
  expires_in: number;
  category: MediaCategory;
  mime_class: string;
}

export interface CompleteResult {
  ok: boolean;
  media: MediaInfo;
  transcript?: string;
}

export function presignMedia(body: {
  filename: string;
  mime: string;
  size: number;
  category: MediaCategory;
}): Promise<PresignResult> {
  return request("/media/presign", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function completeMedia(
  mediaId: string,
  category: MediaCategory,
  mime: string,
): Promise<CompleteResult> {
  return request(`/media/${mediaId}/complete`, {
    method: "POST",
    body: JSON.stringify({ category, mime }),
  });
}

/** Полный цикл загрузки (§9.1): presign → PUT байтов в MinIO → complete. */
export async function uploadFile(
  file: File | Blob,
  category: MediaCategory,
  filename = "blob",
): Promise<CompleteResult> {
  const name = file instanceof File && file.name ? file.name : filename;
  const mime = file.type || "application/octet-stream";
  const presign = await presignMedia({
    filename: name,
    mime,
    size: file.size,
    category,
  });
  const put = await fetch(presign.upload_url, {
    method: "PUT",
    headers: presign.headers,
    body: file,
  });
  if (!put.ok) {
    throw new ApiError(put.status, { error: "upload_failed" });
  }
  return completeMedia(presign.media_id, category, mime);
}

// ── §6.7 трансформация (точки из PWA) ────────────────────────────────────────

/** Точка трансформации по media_id из D3 (consent fail-closed на сервере). */
export function saveBodyCapture(
  mediaId: string,
  caption?: string,
): Promise<{ ok: boolean; capture?: Record<string, unknown>; error?: string }> {
  return request("/transformation/captures", {
    method: "POST",
    body: JSON.stringify({
      media_id: mediaId,
      ...(caption ? { caption } : {}),
    }),
  });
}

export function getTransformation(): Promise<Record<string, unknown>> {
  return request("/transformation/captures");
}


// ── §6.10 Web Push (D4, §11) ─────────────────────────────────────────────────

export interface PushStatus {
  ok: boolean;
  configured: boolean;
  vapid_public_key: string;
  subscriptions: number;
}

export function getPushStatus(): Promise<PushStatus> {
  return request("/push/status");
}

export function subscribePush(sub: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}): Promise<{ ok: boolean }> {
  return request("/push/subscribe", {
    method: "POST",
    body: JSON.stringify(sub),
  });
}

export function unsubscribePush(endpoint: string): Promise<{ ok: boolean }> {
  return request("/push/subscribe", {
    method: "DELETE",
    body: JSON.stringify({ endpoint }),
  });
}

// ── §6.5/§6.6 тренерский workspace (P2): чтение + мутации ────────────────────

/** Ответ gated-мутации: 200 + confirmation_required несёт карточку черновика. */
export interface GatedResult {
  ok: boolean;
  error?: string;
  operation?: OperationCard | null;
  preview?: unknown;
  hint?: string;
}

export interface PulseEvent {
  start_at: string;
  hhmm: string;
  client_name: string | null;
  client_tg_id: number | null;
  is_nika: boolean;
  activity: string | null;
}

export interface PulseClient {
  client_tg_id: number;
  name: string | null;
  silence_days: number | null;
  attention: boolean;
  share_concerns: boolean;
  brief: string | null;
  tasks_open?: number;
}

export interface PulseData {
  ok: boolean;
  error?: string;
  date: string;
  tz: string;
  events: PulseEvent[];
  clients: PulseClient[];
  totals: {
    trainings: number;
    nika_clients: number;
    clients: number;
    attention: number;
  };
}

export interface CoachProgram {
  program_id: number;
  slug: string;
  title: string;
  version: string;
  is_active: boolean;
}

export interface ClientProgramRow {
  client_tg_id: number;
  client_name: string | null;
  assignment_id: number;
  program_title: string;
  version: string;
  owner_type: string;
  phase: string | null;
  phase_index: number | null;
  day_in_phase: number | null;
  started_at: string | null;
}

export interface CoachPrograms {
  ok: boolean;
  error?: string;
  authored: CoachProgram[];
  client_programs: ClientProgramRow[];
}

export interface ProgramDetail {
  ok: boolean;
  error?: string;
  program: {
    program_id: number;
    slug: string;
    title: string;
    version: string;
    owner_type: string;
    is_active: boolean;
    payload: Record<string, unknown>;
    created_by_tg: number;
    created_at: string;
    updated_at: string | null;
  };
  versions: Array<{
    program_id: number;
    version: string;
    title: string;
    is_active: boolean;
    created_at: string | null;
  }>;
}

export interface ScheduleEvent {
  event_id: number;
  start_at: string;
  end_at: string | null;
  activity: string | null;
  location: string | null;
  status: string;
  name: string | null;
  is_nika: boolean;
  client_user_id?: number;
}

export interface ScheduleData {
  ok: boolean;
  error?: string;
  period: string;
  from: string;
  to: string;
  total: number;
  nika: number;
  external: number;
  by_status: Record<string, number>;
  events: ScheduleEvent[];
}

// чтение

export function getCoachPulse(): Promise<PulseData> {
  return request("/coach/pulse");
}

export function getCoachPrograms(): Promise<CoachPrograms> {
  return request("/coach/programs");
}

export function getCoachProgramDetail(programId: number): Promise<ProgramDetail> {
  return request(`/coach/programs/${programId}`);
}

export function getCoachSchedule(
  period: "today" | "tomorrow" | "week" = "today",
): Promise<ScheduleData> {
  return request(`/coach/schedule?period=${encodeURIComponent(period)}`);
}

// gated-мутации (Confirm-Gate: первый вызов → карточка черновика)

export function postCoachSchedule(
  events: Array<Record<string, unknown>>,
  source?: string,
): Promise<GatedResult> {
  return request("/coach/schedule", {
    method: "POST",
    body: JSON.stringify({ events, ...(source ? { source } : {}) }),
  });
}

export function postCoachMessage(clientTg: number, text: string): Promise<GatedResult> {
  return request(`/coach/clients/${clientTg}/message`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

export function postCoachBroadcast(segment: string, text: string): Promise<GatedResult> {
  return request("/coach/broadcast", {
    method: "POST",
    body: JSON.stringify({ segment, text }),
  });
}

export function postProgramSave(body: {
  title: string;
  payload: Record<string, unknown> | string;
  slug?: string;
  program_id?: number;
}): Promise<GatedResult> {
  return request("/programs", { method: "POST", body: JSON.stringify(body) });
}

export function postAdvancePhase(
  assignmentId: number,
  toPhase?: string,
): Promise<GatedResult> {
  return request(`/programs/${assignmentId}/advance-phase`, {
    method: "POST",
    body: JSON.stringify(toPhase ? { to_phase: toPhase } : {}),
  });
}

// данные-мутации (M2: вне гейта, статус-кво Phase 13)

export function patchCoachScheduleEvent(
  eventId: number,
  patch: {
    status?: string;
    start_at?: string;
    end_at?: string;
    location?: string;
    activity?: string;
    weekday_hint?: string;
  },
): Promise<Record<string, unknown>> {
  return request(`/coach/schedule/${eventId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function postCoachNote(
  clientTg: number,
  payload: {
    content?: string;
    note_type?: string;
    visibility?: string;
    entries?: unknown[];
    transcript?: string;
  },
): Promise<Record<string, unknown>> {
  return request(`/coach/clients/${clientTg}/notes`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function postCoachTasks(
  clientTg: number,
  tasks: Array<Record<string, unknown>>,
): Promise<Record<string, unknown>> {
  return request(`/coach/clients/${clientTg}/tasks`, {
    method: "POST",
    body: JSON.stringify({ tasks }),
  });
}

export function completeCoachTask(
  taskId: number,
  status: "done" | "cancelled" = "done",
): Promise<Record<string, unknown>> {
  return request(`/coach/tasks/${taskId}/complete`, {
    method: "POST",
    body: JSON.stringify({ status }),
  });
}

// ── Phase 15 E1: feature-флаги + кошелёк display-only (PWA-07, §12/§14) ─────

export interface FeaturesSnapshot {
  flags: Record<string, { enabled: boolean; source: "override" | "env" }>;
}

export function getFeatures(): Promise<FeaturesSnapshot> {
  return request("/config/features");
}

export interface WalletPayment {
  amount: number;
  currency: string;
  status: string;
  provider: string;
  paid_at: string | null;
}

export interface WalletBonuses {
  ok: boolean;
  invited: number;
  qualified: number;
  credit_pct: number;
  credit: { pending: number; confirmed: number };
  error?: string;
}

export interface WalletSubscription {
  paid_until: string | null;
  access_state: string;
  coach_free_until?: string | null;
  free_forever: boolean;
  free_months_remaining: number;
  discount_percent: number | null;
  monthly_price: number | null;
  base_price: number | null;
  pay_link: string | null;
  // bpcpay: кнопка оплаты открывает hosted-checkout (POST /me/pay-link);
  // telegram: pay_link ведёт в бота.
  pay_mode?: string;
  // QR СБП (РФ, самозанятый): картинка с кодом, сумму вводит человек.
  pay_qr_url?: string | null;
  // Тарифы для подсказки по QR: unit — «₽»/«BYN», plans — месяц/3 месяца/год.
  offer?: {
    unit?: string;
    plans?: { id: string; label: string; price: number; period_days: number }[];
  } | null;
  refund_policy: string;
}

export interface MyWallet {
  ok: boolean;
  payments_enabled: boolean;
  economy_enabled: boolean;
  payouts_enabled: boolean;
  payments: WalletPayment[];
  bonuses?: WalletBonuses | null;
  subscription?: WalletSubscription | null;
}

export function getMyWallet(): Promise<MyWallet> {
  return request("/me/wallet");
}

export interface PayLinkResult {
  ok: boolean;
  url?: string;
  amount?: number;
  currency?: string;
  error?: string;
  fallback_url?: string | null;
}

/** Ссылка на оплату (мигр.055): hosted-checkout PayPro, привязан к аккаунту.
 *  plan — тариф (month|quarter|year): оплата оптом (3 месяца / год). */
export function createPayLink(returnUrl?: string, plan?: string): Promise<PayLinkResult> {
  return request("/me/pay-link", {
    method: "POST",
    body: JSON.stringify({
      ...(returnUrl ? { return_url: returnUrl } : {}),
      ...(plan ? { plan } : {}),
    }),
  });
}

export interface CoachReferralRow {
  referral_code: string;
  status: string;
  client_name: string | null;
  referred_at: string | null;
  converted_at: string | null;
}

export interface CoachBusiness {
  ok: boolean;
  payments_enabled: boolean;
  economy_enabled: boolean;
  payouts_enabled: boolean;
  referral_code?: string | null;
  referral_link?: string | null;
  referrals: {
    ok: boolean;
    referrals?: CoachReferralRow[];
    summary?: { pending: number; converted: number; expired: number };
    error?: string;
  };
  cashback?: {
    ok: boolean;
    clients?: Array<Record<string, unknown>>;
    boost_pct?: number;
    totals?: {
      cashback_pending: number;
      cashback_confirmed: number;
      network_pending: number;
      network_confirmed: number;
    };
    error?: string;
  };
  network?: {
    ok: boolean;
    rate_pct?: number;
    window_months?: number;
    total_referred?: number;
    qualified_count?: number;
    referred?: Array<Record<string, unknown>>;
    error?: string;
  };
}

export function getCoachBusiness(): Promise<CoachBusiness> {
  return request("/coach/business");
}
